import { Show, For, createMemo, createSignal, createEffect, type Component } from "solid-js";
import { PanelLayout } from "@/shared/panel-base";
import { PanelBadge } from "@/shared/panel-primitives";
import { createToolQuery } from "@/shared/query";
import { usePanel } from "@/shared/use-panel";
import { useJobEvents } from "@/shared/use-job-events";
import { registerPanel } from "@/panel-registry";

// ── Types ──────────────────────────────────────────────────────────────────────

interface SshTargetRow {
  id: string;
  label?: string;
  target?: string;
  keyFile?: string;
  allowDestructive?: boolean;
}

interface TargetsListResult {
  count?: number;
  rows: SshTargetRow[];
}

interface TestResult {
  targetId: string;
  reachable: boolean;
  latencyMs?: number;
}

interface ExecJobResult {
  targetId?: string;
  command?: string;
  exitCode?: number;
  stdout?: string;
  stderr?: string;
  stdoutBytes?: number;
  stderrBytes?: number;
  stdoutTruncated?: boolean;
  stderrTruncated?: boolean;
  truncated?: boolean;
  timedOut?: boolean;
  durationMs?: number;
  queuedMs?: number;
  maxConcurrent?: number;
  metadata?: ExecMetadata;
}

interface ExecMetadata {
  stdoutBytes?: number;
  stderrBytes?: number;
  stdoutTruncated?: boolean;
  stderrTruncated?: boolean;
  truncated?: boolean;
  timedOut?: boolean;
  durationMs?: number;
  queuedMs?: number;
  maxConcurrent?: number;
}

interface ExecAsyncAck {
  jobId: string;
  async?: boolean;
  status?: string;
}

interface HistoryEntry {
  id: number;
  command: string;
  ts: number;
  exitCode?: number;
  stdout?: string;
  stderr?: string;
  stdoutBytes?: number;
  stderrBytes?: number;
  stdoutTruncated?: boolean;
  stderrTruncated?: boolean;
  truncated?: boolean;
  timedOut?: boolean;
  durationMs?: number;
  queuedMs?: number;
  maxConcurrent?: number;
  error?: string;
}

// ── localStorage helpers ───────────────────────────────────────────────────────

const HISTORY_KEY_PREFIX = "rws.ssh.history.";
const HISTORY_MAX = 10;

function loadHistory(targetId: string): HistoryEntry[] {
  if (!targetId) return [];
  try {
    const raw = localStorage.getItem(HISTORY_KEY_PREFIX + targetId);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as HistoryEntry[];
    return Array.isArray(parsed) ? parsed.slice(0, HISTORY_MAX) : [];
  } catch {
    return [];
  }
}

function saveHistory(targetId: string, history: HistoryEntry[]): void {
  if (!targetId) return;
  try {
    const trimmed = history.slice(0, HISTORY_MAX);
    localStorage.setItem(HISTORY_KEY_PREFIX + targetId, JSON.stringify(trimmed));
  } catch {
    // quota / privacy mode — ignore
  }
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function errMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

function isConfigMissing(err: unknown): boolean {
  return errMessage(err).includes("ssh_targets_config_missing");
}

function isUnknownTarget(err: unknown): boolean {
  return errMessage(err).includes("ssh_target_unknown");
}

/** Déduplique en gardant la première occurrence (la plus récente). */
function dedupeRecent(items: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const it of items) {
    if (seen.has(it)) continue;
    seen.add(it);
    out.push(it);
  }
  return out;
}

/**
 * Filtre une liste de commandes (du plus récent au plus ancien) par
 * sous-chaîne insensible à la casse. Query vide → toute la liste.
 */
function matchHistory(commands: string[], query: string): string[] {
  const q = query.trim().toLowerCase();
  if (!q) return commands;
  return commands.filter((c) => c.toLowerCase().includes(q));
}

/** Navigue dans un cycle avec wrap modulo. Retourne 0 si total <= 0. */
function cycleIndex(current: number, total: number, delta: number): number {
  if (total <= 0) return 0;
  return (current + delta + total) % total;
}

function mergeExecResult(entry: HistoryEntry, result: ExecJobResult): HistoryEntry {
  const metadata = result.metadata ?? {};
  return {
    ...entry,
    exitCode: result.exitCode,
    stdout: result.stdout ?? "",
    stderr: result.stderr ?? "",
    stdoutBytes: result.stdoutBytes ?? metadata.stdoutBytes,
    stderrBytes: result.stderrBytes ?? metadata.stderrBytes,
    stdoutTruncated: !!(result.stdoutTruncated ?? metadata.stdoutTruncated),
    stderrTruncated: !!(result.stderrTruncated ?? metadata.stderrTruncated),
    truncated: !!(result.truncated ?? metadata.truncated),
    timedOut: !!(result.timedOut ?? metadata.timedOut),
    durationMs: result.durationMs ?? metadata.durationMs,
    queuedMs: result.queuedMs ?? metadata.queuedMs,
    maxConcurrent: result.maxConcurrent ?? metadata.maxConcurrent,
  };
}

// État du menu de recherche inversée d'historique (Ctrl+R).
interface ReverseSearchState {
  query: string;
  index: number;
}

// ── Panel ──────────────────────────────────────────────────────────────────────

const SshPanel: Component = () => {
  const [selectedId, setSelectedId] = createSignal<string>("");
  const [command, setCommand] = createSignal<string>("");
  const [history, setHistory] = createSignal<HistoryEntry[]>([]);
  const [activeJobId, setActiveJobId] = createSignal<string | null>(null);
  const [pendingHistoryId, setPendingHistoryId] = createSignal<number | null>(null);
  const [testing, setTesting] = createSignal(false);
  const [lastTest, setLastTest] = createSignal<TestResult | null>(null);
  const [testError, setTestError] = createSignal<string>("");
  const [submitting, setSubmitting] = createSignal(false);
  // Reverse search d'historique (Ctrl+R)
  const [rsearch, setRsearch] = createSignal<ReverseSearchState | null>(null);
  let commandInputRef: HTMLInputElement | undefined;
  let rsearchRef: HTMLInputElement | undefined;

  // panel doit être déclaré AVANT targetsQuery : createToolQuery évalue ses
  // options de façon synchrone et y référence panel.enabled() (sinon TDZ).
  // usePanel ne lit ses query-thunks que paresseusement (dans status()).
  const panel = usePanel(() => ({ isFetching: targetsQuery.isFetching, error: targetsQuery.error }));

  const targetsQuery = createToolQuery<TargetsListResult>(() => ({
    tool: "ssh_targets_list",
    params: {},
    enabled: panel.enabled(),
    refetchInterval: 0,
    staleTime: 30_000,
  }));

  const jobState = useJobEvents(() => activeJobId());

  const targets = createMemo<SshTargetRow[]>(() => targetsQuery.data?.rows ?? []);
  const configMissing = createMemo(() => isConfigMissing(targetsQuery.error));

  const selectedTarget = createMemo<SshTargetRow | null>(() => {
    const id = selectedId();
    if (!id) return null;
    return targets().find((t) => t.id === id) ?? null;
  });

  // Auto-sélectionne la première cible disponible
  createEffect(() => {
    const list = targets();
    if (list.length === 0) return;
    const current = selectedId();
    if (!current || !list.some((t) => t.id === current)) {
      setSelectedId(list[0].id);
    }
  });

  // Recharge l'historique quand la cible change
  createEffect(() => {
    const id = selectedId();
    setHistory(loadHistory(id));
    setLastTest(null);
    setTestError("");
  });

  // Quand un job se termine, fusionner le résultat dans l'historique
  createEffect(() => {
    const js = jobState();
    const pendingId = pendingHistoryId();
    if (!js || pendingId === null) return;
    if (js.status !== "done" && js.status !== "error") return;

    setHistory((prev) => {
      const next = prev.map((entry) => {
        if (entry.id !== pendingId) return entry;
        if (js.status === "done") {
          const result = (js.result ?? {}) as ExecJobResult;
          return mergeExecResult(entry, result);
        }
        return { ...entry, error: js.error ?? "Erreur inconnue" };
      });
      saveHistory(selectedId(), next);
      return next;
    });

    setPendingHistoryId(null);
    setActiveJobId(null);
    setSubmitting(false);
  });

  async function testConnection(): Promise<void> {
    const id = selectedId();
    if (!id || testing()) return;
    setTesting(true);
    setLastTest(null);
    setTestError("");
    try {
      const r = await panel.tools.call<TestResult>("ssh_test", { targetId: id });
      setLastTest(r);
    } catch (err) {
      setTestError(errMessage(err));
    } finally {
      setTesting(false);
    }
  }

  async function execCommand(): Promise<void> {
    const id = selectedId();
    const cmd = command().trim();
    if (!id || !cmd || submitting()) return;

    const tgt = selectedTarget();
    const ok = await panel.confirm.confirm({
      title: `SSH exec — ${tgt?.label ?? id}`,
      body: `Exécuter sur ${tgt?.target ?? id} :\n\n${cmd}`,
      level: "warn",
      confirmLabel: "Exécuter",
    });
    if (!ok) return;

    const historyId = Date.now();
    const entry: HistoryEntry = { id: historyId, command: cmd, ts: historyId };
    setHistory((prev) => {
      const next = [entry, ...prev].slice(0, HISTORY_MAX);
      saveHistory(id, next);
      return next;
    });

    setSubmitting(true);
    setPendingHistoryId(historyId);

    try {
      const res = await panel.tools.call<ExecAsyncAck | ExecJobResult>(
        "ssh_exec",
        { targetId: id, command: cmd },
        { userConfirmedAt: new Date().toISOString() },
      );

      if (res && typeof res === "object" && "jobId" in res && (res as ExecAsyncAck).jobId) {
        // Réponse async — useJobEvents prendra le relais
        setActiveJobId((res as ExecAsyncAck).jobId);
      } else {
        // Réponse synchrone (fallback) — fusionner directement
        const sync = res as ExecJobResult;
        setHistory((prev) => {
          const next = prev.map((e) => (e.id === historyId ? mergeExecResult(e, sync) : e));
          saveHistory(id, next);
          return next;
        });
        setPendingHistoryId(null);
        setSubmitting(false);
      }
      setCommand("");
    } catch (err) {
      setHistory((prev) => {
        const next = prev.map((e) => (e.id === historyId ? { ...e, error: errMessage(err) } : e));
        saveHistory(id, next);
        return next;
      });
      setPendingHistoryId(null);
      setSubmitting(false);
    }
  }

  function clearHistory(): void {
    const id = selectedId();
    if (!id) return;
    setHistory([]);
    try {
      localStorage.removeItem(HISTORY_KEY_PREFIX + id);
    } catch {
      // ignore
    }
  }

  function reuseCommand(cmd: string): void {
    setCommand(cmd);
  }

  const latestEntry = createMemo<HistoryEntry | null>(() => history()[0] ?? null);

  // ── Reverse search (Ctrl+R) ─────────────────────────────────────────────────

  // Commandes saisies, du plus récent au plus ancien, dédupliquées.
  const commandHistory = createMemo(() =>
    dedupeRecent(
      history()
        .map((e) => e.command)
        .filter((c) => c.trim().length > 0),
    ),
  );

  const rsearchMatches = createMemo(() => {
    const rs = rsearch();
    if (!rs) return [];
    return matchHistory(commandHistory(), rs.query);
  });

  function openReverseSearch(): void {
    if (submitting()) return;
    setRsearch({ query: "", index: 0 });
    setTimeout(() => rsearchRef?.focus(), 0);
  }

  function closeReverseSearch(insert: boolean): void {
    const rs = rsearch();
    const matches = rsearchMatches();
    setRsearch(null);
    if (insert && rs && matches.length > 0) {
      const picked = matches[Math.min(rs.index, matches.length - 1)];
      setCommand(picked);
      setTimeout(() => {
        if (commandInputRef) {
          commandInputRef.setSelectionRange(picked.length, picked.length);
          commandInputRef.focus();
        }
      }, 0);
    } else {
      setTimeout(() => commandInputRef?.focus(), 0);
    }
  }

  function reverseSearchNext(): void {
    const matches = rsearchMatches();
    if (matches.length === 0) return;
    setRsearch((rs) => (rs ? { ...rs, index: cycleIndex(rs.index, matches.length, 1) } : rs));
  }

  function reverseSearchPrev(): void {
    const matches = rsearchMatches();
    if (matches.length === 0) return;
    setRsearch((rs) => (rs ? { ...rs, index: cycleIndex(rs.index, matches.length, -1) } : rs));
  }

  // ── Render helpers ──────────────────────────────────────────────────────────

  const toolbar = (
    <>
      <select
        class="panel-select"
        value={selectedId()}
        disabled={targets().length === 0}
        onChange={(e) => setSelectedId(e.currentTarget.value)}
      >
        <Show when={targets().length === 0}>
          <option value="">— aucune cible —</option>
        </Show>
        <For each={targets()}>
          {(t) => (
            <option value={t.id}>
              {t.label ?? t.id} {t.target ? `(${t.target})` : ""}
            </option>
          )}
        </For>
      </select>

      <button
        class="btn btn--sm"
        onClick={testConnection}
        disabled={!selectedId() || testing()}
        title="Tester la connectivité SSH"
      >
        {testing() ? "Test..." : "🩺 Tester"}
      </button>

      <Show when={lastTest()}>
        {(t) => (
          <PanelBadge
            intent={t().reachable ? "ok" : "danger"}
            title={t().reachable ? "Connexion OK" : "Échec connexion"}
          >
            {t().reachable ? "● online" : "● offline"}
            <Show when={typeof t().latencyMs === "number"}> · {t().latencyMs}ms</Show>
          </PanelBadge>
        )}
      </Show>

      <Show when={testError()}>
        <PanelBadge intent="danger" title={testError()}>
          ⚠ {isUnknownTarget(testError()) ? "cible inconnue" : "erreur test"}
        </PanelBadge>
      </Show>

      <button class="btn btn--xs btn--ghost" onClick={() => targetsQuery.refetch()} title="Recharger les cibles">
        ↺
      </button>
    </>
  );

  return (
    <PanelLayout title="Sessions SSH" toolbar={toolbar} status={panel.status()}>
      <Show
        when={!configMissing()}
        fallback={
          <div class="panel-empty-state">
            <h3>Aucune configuration SSH</h3>
            <p>
              Aucun fichier de configuration SSH trouvé sur l'agent. Pour ajouter des cibles, créer le fichier{" "}
              <code>C:\ProgramData\RemoteWS\ssh-targets.json</code> côté agent avec une liste de hosts (<code>id</code>,{" "}
              <code>target</code> <em>(user@host[:port])</em>, <code>keyFile</code>, <code>allowDestructive</code>).
            </p>
            <button class="btn btn--sm" onClick={() => targetsQuery.refetch()}>
              ↺ Réessayer
            </button>
          </div>
        }
      >
        <Show
          when={targets().length > 0}
          fallback={
            <div class="panel-empty-state">
              <p>Aucune cible SSH déclarée dans la configuration agent.</p>
            </div>
          }
        >
          <div class="ssh-panel">
            {/* Éditeur de commande */}
            <div class="ssh-editor">
              <div class="ssh-editor-row">
                <span class="ssh-editor-prompt">$</span>
                <input
                  ref={commandInputRef}
                  class="ssh-editor-input"
                  type="text"
                  placeholder="Commande à exécuter sur la cible…"
                  value={command()}
                  onInput={(e) => setCommand(e.currentTarget.value)}
                  onKeyDown={(e) => {
                    if ((e.key === "r" || e.key === "R") && e.ctrlKey) {
                      e.preventDefault();
                      openReverseSearch();
                      return;
                    }
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      void execCommand();
                    }
                  }}
                  disabled={submitting()}
                  autocomplete="off"
                  spellcheck={false}
                />
                <button
                  class="btn btn--sm btn--primary"
                  onClick={execCommand}
                  disabled={submitting() || !command().trim() || !selectedId()}
                >
                  {submitting() ? "⏳ ..." : "▶ Exécuter"}
                </button>
              </div>

              <Show when={rsearch()}>
                {(rs) => (
                  <div class="ssh-rsearch">
                    <span class="ssh-rsearch-label">(reverse-i-search)</span>
                    <input
                      ref={rsearchRef}
                      class="ssh-rsearch-input"
                      type="text"
                      value={rs().query}
                      placeholder="rechercher dans l'historique…"
                      autocomplete="off"
                      spellcheck={false}
                      onInput={(e) => {
                        const q = e.currentTarget.value;
                        setRsearch((cur) => (cur ? { ...cur, query: q, index: 0 } : cur));
                      }}
                      onKeyDown={(e) => {
                        if ((e.key === "r" || e.key === "R") && e.ctrlKey) {
                          e.preventDefault();
                          reverseSearchNext();
                        } else if (e.key === "Enter" || e.key === "Tab") {
                          e.preventDefault();
                          closeReverseSearch(true);
                        } else if (e.key === "Escape") {
                          e.preventDefault();
                          closeReverseSearch(false);
                        } else if (e.key === "ArrowDown") {
                          e.preventDefault();
                          reverseSearchNext();
                        } else if (e.key === "ArrowUp") {
                          e.preventDefault();
                          reverseSearchPrev();
                        }
                      }}
                    />
                    <span class="ssh-rsearch-match">
                      <Show
                        when={rsearchMatches().length > 0}
                        fallback={<span class="ssh-rsearch-empty">aucun résultat</span>}
                      >
                        <code>{rsearchMatches()[Math.min(rs().index, rsearchMatches().length - 1)]}</code>
                        <span class="ssh-rsearch-count">
                          {Math.min(rs().index + 1, rsearchMatches().length)}/{rsearchMatches().length}
                        </span>
                      </Show>
                    </span>
                    <button
                      class="btn btn--xs btn--ghost"
                      onClick={() => closeReverseSearch(false)}
                      title="Annuler (Échap)"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </Show>
              <Show when={selectedTarget() && selectedTarget()!.allowDestructive === false}>
                <div class="ssh-warning">
                  ⚠ Cette cible n'autorise pas <code>ssh_exec</code> (<code>allowDestructive: false</code>).
                </div>
              </Show>
            </div>

            {/* Output dernier exec */}
            <div class="ssh-output-section">
              <div class="ssh-output-header">
                <strong>Sortie</strong>
                <Show when={latestEntry()}>
                  {(e) => (
                    <Show
                      when={typeof e().exitCode === "number"}
                      fallback={
                        <Show when={e().error} fallback={<PanelBadge intent="info">en cours…</PanelBadge>}>
                          <PanelBadge intent="danger">erreur</PanelBadge>
                        </Show>
                      }
                    >
                      <PanelBadge intent={e().exitCode === 0 ? "ok" : "warn"} title="Exit code">
                        exit {e().exitCode}
                      </PanelBadge>
                      <Show when={e().timedOut}>
                        <PanelBadge intent="danger">timeout</PanelBadge>
                      </Show>
                      <Show when={typeof e().queuedMs === "number" && e().queuedMs! > 0}>
                        <PanelBadge>queue {e().queuedMs} ms</PanelBadge>
                      </Show>
                      <Show when={e().truncated}>
                        <PanelBadge intent="warn">sortie tronquée</PanelBadge>
                      </Show>
                    </Show>
                  )}
                </Show>
              </div>

              <div class="ssh-output-body">
                <Show when={latestEntry()} fallback={<div class="ssh-output-empty">Aucune commande exécutée.</div>}>
                  {(e) => (
                    <>
                      <div class="ssh-output-cmd">
                        <span class="ssh-editor-prompt">$</span> {e().command}
                      </div>
                      <Show when={e().error}>
                        <pre class="ssh-output-pane ssh-output-pane--err">{e().error}</pre>
                      </Show>
                      <Show when={e().stdout}>
                        <div class="ssh-output-label">
                          stdout
                          <Show when={typeof e().stdoutBytes === "number"}> ({e().stdoutBytes} o)</Show>
                          <Show when={e().stdoutTruncated}> · tronqué</Show>
                        </div>
                        <pre class="ssh-output-pane ssh-output-pane--out">{e().stdout}</pre>
                      </Show>
                      <Show when={e().stderr}>
                        <div class="ssh-output-label">
                          stderr
                          <Show when={typeof e().stderrBytes === "number"}> ({e().stderrBytes} o)</Show>
                          <Show when={e().stderrTruncated}> · tronqué</Show>
                        </div>
                        <pre class="ssh-output-pane ssh-output-pane--err">{e().stderr}</pre>
                      </Show>
                      <Show when={typeof e().exitCode === "number" && !e().error && !e().stdout && !e().stderr}>
                        <pre class="ssh-output-pane ssh-output-pane--muted">(vide)</pre>
                      </Show>
                    </>
                  )}
                </Show>
              </div>

              {/* Logs job streaming */}
              <Show when={jobState() && jobState()!.logs.length > 0}>
                <details class="ssh-job-logs">
                  <summary>
                    Journal job{" "}
                    <PanelBadge
                      intent={jobState()!.status === "done" ? "ok" : jobState()!.status === "error" ? "danger" : "info"}
                    >
                      {jobState()!.status}
                    </PanelBadge>
                  </summary>
                  <pre class="panel-job-log">
                    <For each={jobState()!.logs}>{(line) => <div class="panel-job-log-line">{line}</div>}</For>
                  </pre>
                </details>
              </Show>
            </div>

            {/* Historique */}
            <div class="ssh-history-section">
              <div class="ssh-history-header">
                <strong>
                  Historique ({history().length}/{HISTORY_MAX})
                </strong>
                <Show when={history().length > 0}>
                  <button class="btn btn--xs btn--ghost" onClick={clearHistory}>
                    Vider
                  </button>
                </Show>
              </div>
              <Show
                when={history().length > 0}
                fallback={<div class="ssh-output-empty">Aucune commande dans l'historique.</div>}
              >
                <ul class="ssh-history-list">
                  <For each={history()}>
                    {(entry) => (
                      <li class="ssh-history-item">
                        <button
                          class="ssh-history-cmd"
                          onClick={() => reuseCommand(entry.command)}
                          title="Réutiliser cette commande"
                        >
                          <span class="ssh-editor-prompt">$</span> {entry.command}
                        </button>
                        <span class="ssh-history-meta">
                          <Show when={typeof entry.exitCode === "number"}>
                            <PanelBadge intent={entry.exitCode === 0 ? "ok" : "warn"}>exit {entry.exitCode}</PanelBadge>
                          </Show>
                          <Show when={entry.timedOut}>
                            <PanelBadge intent="danger">timeout</PanelBadge>
                          </Show>
                          <Show when={entry.error}>
                            <PanelBadge intent="danger" title={entry.error}>
                              erreur
                            </PanelBadge>
                          </Show>
                          <Show when={!entry.error && typeof entry.exitCode !== "number"}>
                            <PanelBadge intent="info">en cours…</PanelBadge>
                          </Show>
                          <span class="ssh-history-ts">{new Date(entry.ts).toLocaleTimeString()}</span>
                        </span>
                      </li>
                    )}
                  </For>
                </ul>
              </Show>
            </div>
          </div>

          {/* Styles scoped — alignés sur les conventions des autres panels */}
          <style>{`
            .ssh-panel {
              display: flex;
              flex-direction: column;
              gap: 12px;
              padding: 8px 4px;
            }
            .ssh-editor {
              display: flex;
              flex-direction: column;
              gap: 4px;
            }
            .ssh-editor-row {
              display: flex;
              align-items: center;
              gap: 8px;
            }
            .ssh-editor-prompt {
              font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
              color: var(--accent, #6cf);
              font-weight: 600;
              user-select: none;
            }
            .ssh-editor-input {
              flex: 1;
              font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
              font-size: 13px;
              padding: 6px 10px;
              border-radius: 4px;
              border: 1px solid var(--border, #444);
              background: var(--input-bg, #1a1a1a);
              color: var(--fg, #eee);
            }
            .ssh-editor-input:disabled { opacity: 0.6; }
            .ssh-warning {
              padding: 4px 8px;
              border-radius: 4px;
              background: rgba(255, 180, 0, 0.1);
              color: #ffb400;
              font-size: 12px;
            }

            .ssh-rsearch {
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 6px 10px;
              border-radius: 4px;
              border: 1px solid var(--accent, #6cf);
              background: var(--input-bg, #1a1a1a);
              font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
              font-size: 12px;
            }
            .ssh-rsearch-label {
              flex-shrink: 0;
              color: var(--accent, #6cf);
              font-weight: 600;
              white-space: nowrap;
            }
            .ssh-rsearch-input {
              flex-shrink: 0;
              width: 200px;
              background: transparent;
              border: none;
              border-bottom: 1px solid var(--border, #444);
              color: var(--fg, #eee);
              font-family: inherit;
              font-size: inherit;
              outline: none;
              padding: 2px 0;
            }
            .ssh-rsearch-match {
              flex: 1;
              min-width: 0;
              display: flex;
              align-items: center;
              gap: 8px;
              overflow: hidden;
            }
            .ssh-rsearch-match code {
              flex: 1;
              min-width: 0;
              color: var(--fg, #eee);
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .ssh-rsearch-count {
              flex-shrink: 0;
              color: var(--muted, #888);
              font-variant-numeric: tabular-nums;
            }
            .ssh-rsearch-empty {
              color: var(--muted, #888);
              font-style: italic;
            }

            .ssh-output-section {
              display: flex;
              flex-direction: column;
              gap: 6px;
              border: 1px solid var(--border, #333);
              border-radius: 6px;
              padding: 8px;
              background: var(--panel-bg, #111);
            }
            .ssh-output-header {
              display: flex;
              align-items: center;
              gap: 8px;
              padding-bottom: 4px;
              border-bottom: 1px solid var(--border, #2a2a2a);
            }
            .ssh-output-body {
              display: flex;
              flex-direction: column;
              gap: 4px;
              max-height: 360px;
              overflow-y: auto;
            }
            .ssh-output-empty {
              padding: 12px;
              color: var(--muted, #888);
              font-style: italic;
              text-align: center;
            }
            .ssh-output-cmd {
              font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
              font-size: 12px;
              color: var(--muted, #aaa);
              padding: 2px 4px;
            }
            .ssh-output-label {
              font-size: 11px;
              text-transform: uppercase;
              letter-spacing: 0.04em;
              color: var(--muted, #888);
              margin-top: 4px;
            }
            .ssh-output-pane {
              font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
              font-size: 12px;
              white-space: pre-wrap;
              word-break: break-word;
              padding: 6px 8px;
              border-radius: 4px;
              margin: 0;
              max-height: 200px;
              overflow-y: auto;
            }
            .ssh-output-pane--out {
              background: rgba(80, 200, 120, 0.06);
              color: #c8f0d4;
              border-left: 2px solid #5ac878;
            }
            .ssh-output-pane--err {
              background: rgba(220, 80, 80, 0.06);
              color: #f4c2c2;
              border-left: 2px solid #d05050;
            }
            .ssh-output-pane--muted {
              background: var(--input-bg, #1a1a1a);
              color: var(--muted, #888);
              font-style: italic;
            }

            .ssh-job-logs { margin-top: 4px; }
            .ssh-job-logs summary {
              cursor: pointer;
              font-size: 12px;
              color: var(--muted, #888);
              display: flex;
              align-items: center;
              gap: 8px;
            }

            .ssh-history-section {
              display: flex;
              flex-direction: column;
              gap: 4px;
              border: 1px solid var(--border, #333);
              border-radius: 6px;
              padding: 8px;
              background: var(--panel-bg, #111);
            }
            .ssh-history-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            .ssh-history-list {
              list-style: none;
              margin: 0;
              padding: 0;
              display: flex;
              flex-direction: column;
              gap: 2px;
            }
            .ssh-history-item {
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 4px 6px;
              border-radius: 3px;
            }
            .ssh-history-item:hover { background: var(--hover, rgba(255, 255, 255, 0.04)); }
            .ssh-history-cmd {
              flex: 1;
              background: none;
              border: none;
              color: var(--fg, #eee);
              font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
              font-size: 12px;
              text-align: left;
              cursor: pointer;
              padding: 2px 0;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .ssh-history-meta {
              display: flex;
              align-items: center;
              gap: 6px;
              flex-shrink: 0;
            }
            .ssh-history-ts {
              font-size: 11px;
              color: var(--muted, #888);
              font-variant-numeric: tabular-nums;
            }

            .panel-empty-state {
              padding: 24px;
              text-align: center;
              color: var(--muted, #aaa);
            }
            .panel-empty-state h3 {
              margin: 0 0 8px;
              color: var(--fg, #eee);
            }
            .panel-empty-state code {
              background: var(--input-bg, #1a1a1a);
              padding: 2px 6px;
              border-radius: 3px;
              font-size: 12px;
            }
          `}</style>
        </Show>
      </Show>
    </PanelLayout>
  );
};

registerPanel("ssh", SshPanel);

export default SshPanel;
